<template>
  <div v-if="shouldShowModal" class="global-upload-status">
    <div class="upload-status-container">
      <div class="upload-status-header">
        <div class="upload-status-title">
          <i class="fas fa-cloud-upload-alt"></i>
          <span>Uploads em andamento</span>
        </div>
        <div class="upload-status-actions">
          <button @click="minimizeUploads" class="minimize-btn">
            <i :class="isMinimized ? 'fas fa-expand' : 'fas fa-minus'"></i>
          </button>


        </div>
      </div>
      
      <div v-if="!isMinimized" class="upload-status-content">
        <div v-for="upload in activeUploads" :key="upload.id" class="upload-item" :class="{ 'is-queued': upload.status === 'queued' }">
          <div class="upload-info">
            <div class="upload-filename">
              <i :class="upload.status === 'queued' ? 'fas fa-clock' : 'fas fa-video'"></i>
              <span>{{ upload.filename }}</span>
            </div>
            <div class="upload-details">
              <span class="upload-size">{{ formatFileSize(upload.size) }}</span>
              <span class="upload-speed" v-if="upload.speed > 0">{{ formatSpeed(upload.speed) }}</span>
            </div>
          </div>

          <div class="upload-progress queued" v-if="upload.status === 'queued'">
            <span class="queued-badge"><i class="fas fa-clock"></i> Aguardando na fila</span>
          </div>
          <div class="upload-progress" v-else>
            <div class="progress-bar">
              <div 
                class="progress-fill" 
                :class="getProgressClass(upload.status)"
                :style="{ width: Math.min(upload.progress, 100) + '%' }"
              ></div>
            </div>
            <div class="progress-text">
              <span class="progress-percentage">{{ Math.min(upload.progress, 100) }}%</span>
              <span class="upload-status">{{ getStatusText(upload.status) }}</span>
            </div>
          </div>
          
          <div class="upload-actions">
            <button 
              v-if="upload.status === 'paused'" 
              @click="resumeUpload(upload.id)" 
              class="action-btn resume-btn"
            >
              <i class="fas fa-play"></i>
            </button>
            <button
              v-if="upload.status === 'uploading' || upload.status === 'processing' || upload.status === 'canceling'"
              @click="cancelUpload(upload.id)"
              class="action-btn cancel-btn"
              :disabled="upload.status === 'canceling'"
              title="Cancelar upload"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        
        <div v-if="completedUploads.length > 0" class="completed-uploads">
          <div class="completed-header">
            <span>Uploads concluídos</span>
          </div>
          <div v-for="upload in completedUploads" :key="upload.id" class="upload-item completed">
            <div class="upload-info">
              <div class="upload-filename">
                <i class="fas fa-check-circle"></i>
                <span>{{ upload.filename }}</span>
              </div>
            </div>
            <div class="upload-status completed">
              <span>Concluído</span>
            </div>
          </div>
        </div>
        
        <div v-if="failedUploads.length > 0" class="failed-uploads">
          <div class="failed-header">
            <span>Uploads falharam</span>
          </div>
          <div v-for="upload in failedUploads" :key="upload.id" class="upload-item failed">
            <div class="upload-info">
              <div class="upload-filename">
                <i class="fas fa-exclamation-triangle"></i>
                <span>{{ upload.filename }}</span>
              </div>
              <div class="upload-error">{{ upload.error }}</div>
            </div>
            <div class="upload-actions">
              <button @click="retryUpload(upload.id)" class="action-btn retry-btn">
                <i class="fas fa-redo"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else class="upload-status-minimized">
        <div class="minimized-info">
          <i class="fas fa-cloud-upload-alt"></i>
          <span>{{ activeUploads.length }} upload(s) ativo(s)</span>
          <div class="minimized-progress">
            <div class="progress-bar">
              <div 
                class="progress-fill" 
                :style="{ width: Math.min(totalProgress, 100) + '%' }"
              ></div>
            </div>
            <span>{{ Math.min(totalProgress, 100) }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import files from '@/utils/files';

export default {
  name: 'GlobalUploadStatus',
  data() {
    return {
      isMinimized: false,
      isExiting: false
    };
  },
  computed: {
    ...mapState('upload', ['isUploading']),
    ...mapGetters('upload', ['activeUploads', 'completedUploads', 'failedUploads', 'totalProgress']),
    shouldShowModal() {
      // Mostrar modal se houver uploads ativos OU uploads completos/falhados (ainda não removidos)
      return this.isUploading || this.completedUploads.length > 0 || this.failedUploads.length > 0;
    }
  },
  mounted() {
    window.addEventListener('pagehide', this.cancelUploadsOnExit);
    window.addEventListener('beforeunload', this.confirmBeforeExit);
  },
  beforeUnmount() {
    window.removeEventListener('pagehide', this.cancelUploadsOnExit);
    window.removeEventListener('beforeunload', this.confirmBeforeExit);
  },
  methods: {
    ...mapActions('upload', ['removeUpload', 'clearCompleted']),

    markUploadCancelRequested(upload) {
      upload.status = 'canceling';
      upload.cancelRequested = true;
      upload.speed = 0;
    },

    confirmBeforeExit(event) {
      const hasActiveUploads = (this.$store.state.upload.uploads || [])
        .some((upload) => ['uploading', 'processing', 'canceling'].includes(upload.status));

      if (!hasActiveUploads) return;

      event.preventDefault();
      event.returnValue = '';
    },

    cancelUploadsOnExit() {
      if (this.isExiting) return;
      this.isExiting = true;

      const token = this.$store.state.token;
      if (!token) return;

      const uploads = this.$store.state.upload.uploads || [];
      uploads
        .filter((upload) => ['uploading', 'processing', 'canceling'].includes(upload.status))
        .map((upload) => upload.serverFileId || upload.fileId || upload.videoId)
        .filter(Boolean)
        .forEach((fileId) => {
          files.cancelUploadOnUnload(fileId, token);
        });
    },

    async waitForBackendFileId(uploadId, maxAttempts = 20, delayMs = 300) {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const upload = this.$store.state.upload.uploads.find((u) => u.id === uploadId);
        if (!upload) return null;

        const backendFileId = upload.serverFileId || upload.fileId || upload.videoId || null;
        if (backendFileId) return backendFileId;

        if (!upload.cancelRequested) return null;

        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      return null;
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    formatSpeed(bytesPerSecond) {
      if (bytesPerSecond === 0) return '0 B/s';
      const k = 1024;
      const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
      const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
      return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    getProgressClass(status) {
      switch (status) {
        case 'queued': return 'queued';
        case 'uploading': return 'uploading';
        case 'processing': return 'processing';
        case 'canceling': return 'paused';
        case 'completed': return 'completed';
        case 'failed': return 'failed';
        case 'paused': return 'paused';
        default: return 'uploading';
      }
    },
    
    getStatusText(status) {
      switch (status) {
        case 'queued': return 'Aguardando na fila';
        case 'uploading': return 'Enviando...';
        case 'processing': return 'Processando...';
        case 'canceling': return 'Cancelando...';
        case 'completed': return 'Concluído';
        case 'failed': return 'Falhou';
        case 'paused': return 'Pausado';
        default: return 'Enviando...';
      }
    },
    
    minimizeUploads() {
      this.isMinimized = !this.isMinimized;
    },
    
    pauseUpload(uploadId) {
      this.$store.dispatch('upload/updateStatus', { id: uploadId, status: 'paused' });
    },
    
    resumeUpload(uploadId) {
      this.$store.dispatch('upload/updateStatus', { id: uploadId, status: 'uploading' });
    },
    
    async cancelUpload(uploadId) {
      if (!confirm('Tem certeza que deseja cancelar este upload?')) return;

      const upload = this.$store.state.upload.uploads.find((u) => u.id === uploadId);
      if (!upload) return;

      // Mantem cancelRequested = true sem reverter em caso de falha: e esse flag que faz o
      // abort do XHR e o proximo checkpoint local (ensureUploadNotCanceled) removerem o
      // upload de verdade. Reverter aqui fazia o upload "ressuscitar" e continuar rodando
      // sem aparecer na tela, mesmo depois do usuario confirmar o cancelamento.
      this.markUploadCancelRequested(upload);

      try {
        const currentBackendId = upload.serverFileId || upload.fileId || upload.videoId || null;
        const backendFileId = currentBackendId || await this.waitForBackendFileId(uploadId);

        if (backendFileId) {
          console.info('Cancelando upload no backend:', backendFileId);
          await files.cancelUpload(backendFileId, this.$store.state.token);
          this.$store.dispatch('filesCache/clearFilesCache');
          window.dispatchEvent(new CustomEvent('upload-canceled', {
            detail: { fileId: backendFileId }
          }));
        }
        // Nao removemos a entrada aqui: se removermos antes do fluxo local (uploadFileInstance)
        // checar cancelRequested via ensureUploadNotCanceled, essa checagem nao encontra mais o
        // upload na store, acha que nao foi cancelado e segue enviando/finalizando por baixo dos
        // panos - e e isso que gerava o erro "não foi possível concluir o upload" depois.
        // Quem remove de fato e o proprio fluxo local, assim que atingir o proximo checkpoint.
      } catch (error) {
        console.error('Erro ao cancelar upload no backend (cancelamento local segue marcado):', error);
      }
    },
    
    retryUpload(uploadId) {
      if (this.activeUploads.length > 0) {
        alert('Já existe um upload em andamento. Aguarde a conclusão do upload atual.');
        return;
      }
      
      const upload = this.failedUploads.find(u => u.id === uploadId);
      if (upload) {
        this.$store.dispatch('upload/startUpload', {
          file: upload.file,
          filename: upload.filename
        });
        this.removeUpload(uploadId);
      }
    },

    demoUpload() {
      if (this.activeUploads.length > 0) {
        alert('Já existe um upload em andamento. Aguarde a conclusão do upload atual.');
        return;
      }
      
      const demoFile = {
        name: 'demo-video.mp4',
        size: 1024 * 1024 * 5, // 5MB
        type: 'video/mp4'
      };
      
      const uploadId = this.$store.dispatch('upload/startUpload', {
        file: demoFile,
        filename: demoFile.name
      });
      
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 10;
        
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          
          this.$store.dispatch('upload/completeUpload', uploadId);
        } else {
          this.$store.dispatch('upload/updateProgress', {
            id: uploadId,
            progress: Math.round(progress),
            uploadedSize: Math.round((progress / 100) * demoFile.size),
            speed: Math.random() * 1024 * 1024 // 1MB/s simulado
          });
        }
      }, 200);
    }
  }
};
</script>

<style lang="scss" scoped>
.global-upload-status {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  max-width: 400px;
  min-width: 350px;
}

.upload-status-container {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid #e1e5e9;
  overflow: hidden;
  transition: all 0.3s ease;
}

.upload-status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f5870f 0%, #ff6b35 100%);
  color: white;
}

.upload-status-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  
  i {
    font-size: 16px;
  }
}

.upload-status-actions {
  display: flex;
  gap: 8px;
  
  button {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 6px;
    width: 28px;
    height: 28px;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

.upload-status-content {
  max-height: 400px;
  overflow-y: auto;
}

.upload-item {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &.completed {
    background: #f8fff9;
    border-left: 4px solid #28a745;
  }
  
  &.failed {
    background: #fff8f8;
    border-left: 4px solid #dc3545;
  }

  &.is-queued {
    opacity: 0.7;
    border-left: 4px solid #f5870f;
  }
}

.upload-info {
  margin-bottom: 12px;
}

.upload-filename {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
  
  i {
    color: #f5870f;
    font-size: 12px;
  }
}

.upload-details {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #666;
}

.upload-progress {
  margin-bottom: 12px;
}

.upload-progress.queued {
  margin-bottom: 0;
}

.queued-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #f5870f;
  font-weight: 600;
  font-size: 12px;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  transition: width 0.3s ease;
  
  &.uploading {
    background: linear-gradient(90deg, #f5870f, #ff6b35);
  }
  
  &.processing {
    background: linear-gradient(90deg, #17a2b8, #20c997);
  }
  
  &.completed {
    background: #28a745;
  }
  
  &.failed {
    background: #dc3545;
  }
  
  &.paused {
    background: #6c757d;
  }
}

.progress-text {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.progress-percentage {
  font-weight: 600;
  color: #333;
}

.upload-status {
  color: #666;
}

.upload-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.action-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover {
    border-color: #f5870f;
    color: #f5870f;
  }
  
  &.pause-btn:hover {
    border-color: #ffc107;
    color: #ffc107;
  }
  
  &.resume-btn:hover {
    border-color: #28a745;
    color: #28a745;
  }
  
  &.cancel-btn:hover {
    border-color: #dc3545;
    color: #dc3545;
  }
  
  &.retry-btn:hover {
    border-color: #17a2b8;
    color: #17a2b8;
  }
}

.completed-uploads,
.failed-uploads {
  border-top: 1px solid #e1e5e9;
}

.completed-header,
.failed-header {
  padding: 12px 20px;
  background: #f8f9fa;
  font-weight: 600;
  font-size: 13px;
  color: #495057;
  border-bottom: 1px solid #e1e5e9;
}

.upload-error {
  font-size: 11px;
  color: #dc3545;
  margin-top: 4px;
}

.upload-status-minimized {
  padding: 12px 20px;
  background: #f8f9fa;
}

.minimized-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #495057;
}

.minimized-progress {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 80px;
}

.dark-mode .upload-status-container {
  background: #2d3748;
  border-color: #4a5568;
}

.dark-mode .upload-item {
  border-bottom-color: #4a5568;
  
  &.completed {
    background: #1a202c;
    border-left-color: #48bb78;
  }
  
  &.failed {
    background: #1a202c;
    border-left-color: #f56565;
  }
}

.dark-mode .upload-filename {
  color: #e2e8f0;
}

.dark-mode .upload-details {
  color: #a0aec0;
}

.dark-mode .completed-header,
.dark-mode .failed-header,
.dark-mode .upload-status-minimized {
  background: #1a202c;
  color: #e2e8f0;
  border-color: #4a5568;
}

@media (max-width: 768px) {
  .global-upload-status {
    bottom: 10px;
    left: 10px;
    right: 10px;
    max-width: none;
    min-width: auto;
  }
  
  .upload-status-container {
    border-radius: 8px;
  }
  
  .upload-status-header {
    padding: 12px 16px;
  }
  
  .upload-item {
    padding: 12px 16px;
  }
}
</style>
